import * as React from 'react';
import { styled } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Check from '@mui/icons-material/Check';
import StepConnector, { stepConnectorClasses } from '@mui/material/StepConnector';
import { StepIconProps } from '@mui/material/StepIcon';
import useTailwindBrand from '../../Theme/useTailwindBrand';

export interface StepConfig {
    label: string;
    subtitle?: string;
    icon?: React.ReactNode;
    stepNumber?: number;
}

interface BrandStepperProps {
    steps: StepConfig[];
    activeStep: number;
    orientation?: 'horizontal' | 'vertical';
    alternativeLabel?: boolean;
    variant?: 'default' | 'vertical';
}

const BrandStepper: React.FC<BrandStepperProps> = ({ 
    steps, 
    activeStep, 
    orientation = 'horizontal',
    alternativeLabel = true,
    variant = 'default'
}) => {
    const { brandConfig, classes } = useTailwindBrand();
    const primaryColor = brandConfig.colors.primary;

    // Connector personnalisé avec les couleurs de la marque
    const BrandConnector = styled(StepConnector)(({ theme }) => ({
        [`&.${stepConnectorClasses.alternativeLabel}`]: {
            top: 25,
            left: 'calc(-50% + 16px)',
            right: 'calc(50% + 16px)',
        },
        [`&.${stepConnectorClasses.active}`]: {
            [`& .${stepConnectorClasses.line}`]: {
                borderColor: primaryColor,
            },
        },
        [`&.${stepConnectorClasses.completed}`]: {
            [`& .${stepConnectorClasses.line}`]: {
                borderColor: primaryColor,
            },
        },
        [`& .${stepConnectorClasses.line}`]: {
            borderColor: '#eaeaf0',
            borderTopWidth: 3,
            borderRadius: 1,
            ...theme.applyStyles('dark', {
                borderColor: theme.palette.grey[800],
            }),
        },
    }));

    // StepIcon personnalisé avec les couleurs de la marque
    const BrandStepIconRoot = styled('div')<{ ownerState: { active?: boolean; completed?: boolean } }>(
        ({ theme, ownerState }) => ({
            backgroundColor: '#ccc',
            zIndex: 1,
            color: '#fff',
            width: 50,
            height: 50,
            display: 'flex',
            borderRadius: '50%',
            justifyContent: 'center',
            alignItems: 'center',
            transition: 'all 0.3s ease',
            ...theme.applyStyles('dark', {
                backgroundColor: theme.palette.grey[700],
            }),
            ...(ownerState.active && {
                backgroundColor: primaryColor,
                boxShadow: `0 4px 10px 0 ${primaryColor}40`,
                transform: 'scale(1.1)',
            }),
            ...(ownerState.completed && {
                backgroundColor: primaryColor,
            }),
        }),
    );

    function BrandStepIcon(props: StepIconProps) {
        const { active, completed, className, icon } = props;
        const stepIndex = typeof icon === 'number' ? icon - 1 : parseInt(String(icon)) - 1;
        const stepConfig = steps[stepIndex];

        return (
            <BrandStepIconRoot ownerState={{ completed, active }} className={className}>
                {completed ? (
                    <Check style={{ fontSize: '24px', color: 'white' }} />
                ) : stepConfig?.icon ? (
                    <div style={{ color: active ? 'white' : '#9CA3AF' }}>
                        {stepConfig.icon}
                    </div>
                ) : (
                    <div 
                        style={{ 
                            width: 8, 
                            height: 8, 
                            borderRadius: '50%', 
                            backgroundColor: active ? 'white' : '#9CA3AF' 
                        }} 
                    />
                )}
            </BrandStepIconRoot>
        );
    }

    // Pour le variant vertical
    if (variant === 'vertical' || orientation === 'vertical') {
        return (
            <div className={`w-full ${classes.fontFamily}`}>
                <h3 className={`${classes.primaryText} ${classes.fontFamily} font-bold text-lg mb-6 hidden lg:block`}>
                    Progression
                </h3>
                <div className="space-y-4">
                    {steps.map((stepConfig, index) => {
                        const isActive = activeStep >= index;
                        const isCurrent = activeStep === index;
                        const isLast = index === steps.length - 1;

                        return (
                            <div key={index} className="relative">
                                {/* Connector Line */}
                                {!isLast && (
                                    <div 
                                        className={`absolute left-6 top-12 w-0.5 h-12 transition-colors duration-300 ${
                                            isActive ? '' : 'bg-gray-200'
                                        }`}
                                        style={{ 
                                            backgroundColor: isActive ? primaryColor : undefined 
                                        }}
                                    />
                                )}

                                {/* Step Content */}
                                <div className="flex items-start gap-4">
                                    {/* Step Circle */}
                                    <div 
                                        className={`relative flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 ${
                                            isCurrent ? 'shadow-lg scale-110' : ''
                                        }`}
                                        style={{
                                            backgroundColor: isCurrent || isActive ? primaryColor : '#eaeaf0',
                                        }}
                                    >
                                        <div style={{ color: isCurrent || isActive ? 'white' : '#9CA3AF' }}>
                                            {isActive && !isCurrent ? (
                                                <Check style={{ fontSize: '20px' }} />
                                            ) : stepConfig.icon ? (
                                                stepConfig.icon
                                            ) : (
                                                <div style={{ fontSize: '20px' }}>{index + 1}</div>
                                            )}
                                        </div>
                                        {isCurrent && (
                                            <div 
                                                className="absolute inset-0 rounded-full animate-ping opacity-20"
                                                style={{ backgroundColor: primaryColor }}
                                            />
                                        )}
                                    </div>

                                    {/* Step Text */}
                                    <div className="flex-1 pt-1">
                                        <div 
                                            className={`${classes.fontFamily} font-semibold text-sm transition-colors duration-300 ${
                                                isCurrent || isActive ? classes.primaryText : 'text-gray-400'
                                            }`}
                                        >
                                            {stepConfig.label}
                                        </div>
                                        {stepConfig.subtitle && (
                                            <div 
                                                className={`${classes.fontFamily} text-xs mt-0.5 transition-colors duration-300 ${
                                                    isCurrent || isActive ? 'text-gray-600' : 'text-gray-400'
                                                }`}
                                            >
                                                {stepConfig.subtitle}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // Pour le variant horizontal (par défaut)
    return (
        <Stack sx={{ width: '100%' }} spacing={4}>
            <Stepper 
                alternativeLabel={alternativeLabel} 
                activeStep={activeStep} 
                connector={<BrandConnector />}
                orientation={orientation}
            >
                {steps.map((stepConfig, index) => (
                    <Step key={index}>
                        <StepLabel 
                            StepIconComponent={BrandStepIcon}
                            StepIconProps={{ icon: index + 1 }}
                        >
                            <div className={classes.fontFamily}>
                                <div className="font-semibold text-sm">{stepConfig.label}</div>
                                {stepConfig.subtitle && (
                                    <div className="text-xs text-gray-500 mt-0.5">{stepConfig.subtitle}</div>
                                )}
                            </div>
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>
        </Stack>
    );
};

export default BrandStepper;
